<template>
  <div>
    <Card>
      <p slot="title">首页模块管理</p>
      <div class="search-con search-col">
        <Input v-model="searchKey" clearable placeholder="搜索模块名称..." style="width: 300px; margin-right: 10px" />
        <Button type="primary" icon="ios-search" @click="handleSearch">搜索</Button>
        <Button type="success" icon="md-add" @click="handleAdd" style="margin-left: 10px">新增模块</Button>
      </div>

      <Table :columns="columns" :data="tableData" :loading="loading" border style="margin-top: 20px">
        <template slot-scope="{ row }" slot="icon">
          <img v-if="row.module_icon" :src="row.module_icon" style="width: 40px; height: 40px; object-fit: contain" />
          <span v-else>-</span>
        </template>

        <template slot-scope="{ row }" slot="color">
          <div style="display: flex; align-items: center;">
            <div :style="{
              width: '30px',
              height: '30px',
              backgroundColor: row.module_color || '#ccc',
              borderRadius: '4px',
              marginRight: '8px',
              border: '1px solid #dcdee2'
            }"></div>
            <span>{{ row.module_color || '-' }}</span>
          </div>
        </template>

        <template slot-scope="{ row }" slot="funcType">
          <Tag :color="row.action_type === 1 ? 'blue' : 'green'">
            {{ row.action_type === 1 ? '页面跳转' : '跳转其他小程序' }}
          </Tag>
        </template>

        <template slot-scope="{ row }" slot="status">
          <Badge :status="getStatusBadge(row.status)" :text="getStatusText(row.status)" />
        </template>

        <template slot-scope="{ row, index }" slot="action">
          <Button type="primary" size="small" style="margin-right: 5px" @click="handleEdit(row)">编辑</Button>
          <Poptip
            confirm
            title="确定要删除这个模块吗？"
            @on-ok="handleDelete(row)"
            transfer>
            <Button type="error" size="small">删除</Button>
          </Poptip>
        </template>
      </Table>

      <div style="margin: 10px 0; overflow: hidden">
        <div style="float: right;">
          <Page
            :total="pageTotal"
            :current="pageNum"
            :page-size="pageSize"
            :page-size-opts="[10, 20, 30, 50]"
            show-sizer
            show-total
            show-elevator
            @on-change="changePage"
            @on-page-size-change="handlePageSize" />
        </div>
      </div>
    </Card>

    <!-- 新增/编辑弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      :loading="modalLoading"
      :mask-closable="false"
      width="600">
      <Form ref="moduleForm" :model="formData" :rules="formRules" :label-width="100">
        <FormItem label="模块名称" prop="module_name">
          <Input v-model="formData.module_name" placeholder="请输入模块名称" maxlength="20" show-word-limit />
        </FormItem>

        <FormItem label="图标名称" prop="module_icon">
          <Input v-model="formData.module_icon" placeholder="请输入uni-icons图标名称，如：sound" />
          <div style="color: #999; font-size: 12px; margin-top: 5px">
            常用图标：sound, wallet, paperplane, person, home, settings 等
            <a href="https://uniapp.dcloud.net.cn/component/uniui/uni-icons.html" target="_blank" style="color: #0066cc">查看完整图标列表</a>
          </div>
        </FormItem>

        <FormItem label="颜色" prop="module_color">
          <div style="display: flex; align-items: center;">
            <Input v-model="formData.module_color" placeholder="#FFFFFF" style="width: 150px; margin-right: 10px" />
            <ColorPicker v-model="formData.module_color" recommend />
          </div>
        </FormItem>

        <FormItem label="图标颜色" prop="icon_color">
          <ColorPicker v-model="formData.icon_color" />
          <div style="color: #999; font-size: 12px; margin-top: 5px">
            设置uni-icons图标的颜色（默认白色）
          </div>
        </FormItem>

        <FormItem label="功能类型" prop="action_type">
          <RadioGroup v-model="formData.action_type">
            <Radio :label="1">页面跳转</Radio>
            <Radio :label="2">跳转其他小程序</Radio>
          </RadioGroup>
        </FormItem>

        <FormItem v-if="formData.action_type === 1" label="功能值" prop="action_value">
          <Input
            v-model="formData.action_value"
            placeholder="请输入页面路径，如：/pages/index"
          />
        </FormItem>

        <!-- 小程序跳转配置 -->
        <template v-if="formData.action_type === 2">
          <FormItem label="小程序AppID" prop="extra_data.appId">
            <Input
              v-model="formData.extra_data.appId"
              placeholder="请输入目标小程序的AppID，如：wxXXXXXXXX"
              maxlength="50"
            />
          </FormItem>

          <FormItem label="小程序页面路径" prop="extra_data.path">
            <Input
              v-model="formData.extra_data.path"
              placeholder="请输入目标页面路径，如：/pages/index/index"
            />
          </FormItem>

          <FormItem label="传递数据" prop="extra_data.extraData">
            <Input
              v-model="extraDataString"
              type="textarea"
              :rows="4"
              placeholder='请输入JSON格式的数据，如：{"source":"home"}'
              @on-blur="validateExtraData"
            />
            <div v-if="extraDataError" style="color: #ed4014; font-size: 12px; margin-top: 5px">
              {{ extraDataError }}
            </div>
          </FormItem>
        </template>

        <FormItem label="状态" prop="status">
          <RadioGroup v-model="formData.status">
            <Radio :label="1">正常</Radio>
            <Radio :label="2">隐藏</Radio>
            <Radio :label="3">停用</Radio>
          </RadioGroup>
        </FormItem>

        <FormItem label="排序" prop="sort_order">
          <InputNumber v-model="formData.sort_order" :min="0" :max="999" style="width: 150px" />
          <span style="margin-left: 10px; color: #999">数字越小越靠前</span>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" :loading="submitLoading" @click="handleSubmit">提交</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getHomeModuleList,
  createHomeModule,
  updateHomeModule,
  deleteHomeModule
} from '@/api/system/homeModule'

export default {
  name: 'HomeModule',
  data () {
    return {
      searchKey: '',
      loading: false,
      tableData: [],
      pageNum: 1,
      pageSize: 10,
      pageTotal: 0,

      // 表格列配置
      columns: [
        {
          title: '模块名称',
          key: 'module_name',
          minWidth: 120,
          sortable: true
        },
        {
          title: '图标',
          slot: 'icon',
          width: 80,
          align: 'center'
        },
        {
          title: '颜色',
          slot: 'color',
          width: 150,
          align: 'center'
        },
        {
          title: '功能类型',
          slot: 'funcType',
          width: 120,
          align: 'center'
        },
        {
          title: '功能值',
          key: 'action_value',
          minWidth: 200,
          ellipsis: true,
          tooltip: true
        },
        {
          title: '状态',
          slot: 'status',
          width: 100,
          align: 'center'
        },
        {
          title: '排序',
          key: 'sort_order',
          width: 80,
          align: 'center',
          sortable: true
        },
        {
          title: '操作',
          slot: 'action',
          width: 160,
          align: 'center',
          fixed: 'right'
        }
      ],

      // 弹窗相关
      modalVisible: false,
      modalTitle: '新增模块',
      modalLoading: true,
      submitLoading: false,
      isEdit: false,

      // 表单数据
      formData: {
        id: null,
        module_name: '',
        module_icon: '',
        module_color: '#1890FF',
        icon_color: '#FFFFFF',
        action_type: 1,
        action_value: '',
        extra_data: {
          appId: '',
          path: '',
          extraData: {}
        },
        status: 1,
        sort_order: 0
      },

      // 额外数据的字符串形式（用于 textarea 绑定）
      extraDataString: '',
      extraDataError: '',

      // 表单验证规则
      formRules: {
        module_name: [
          { required: true, message: '请输入模块名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        module_icon: [
          { required: true, message: '请输入图标名称', trigger: 'blur' },
          { min: 2, max: 50, message: '图标名称长度在 2-50 个字符', trigger: 'blur' }
        ],
        module_color: [
          { required: true, message: '请选择颜色', trigger: 'change' },
          { pattern: /^#[0-9A-Fa-f]{6}$/, message: '请输入正确的颜色格式', trigger: 'blur' }
        ],
        icon_color: [
          { required: true, message: '请选择图标颜色', trigger: 'change' },
          { pattern: /^#[0-9A-Fa-f]{6}$/, message: '请输入正确的颜色格式', trigger: 'blur' }
        ],
        action_type: [
          { required: true, type: 'number', message: '请选择功能类型', trigger: 'change' }
        ],
        action_value: [
          {
            validator: (rule, value, callback) => {
              if (this.formData.action_type === 1) {
                if (!value) {
                  callback(new Error('请输入功能值'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        'extra_data.appId': [
          {
            validator: (rule, value, callback) => {
              if (this.formData.action_type === 2) {
                if (!value) {
                  callback(new Error('请输入小程序AppID'))
                } else if (!value.startsWith('wx')) {
                  callback(new Error('AppID格式不正确，应以wx开头'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        status: [
          { required: true, type: 'number', message: '请选择状态', trigger: 'change' }
        ],
        sort_order: [
          { required: true, type: 'number', message: '请输入排序值', trigger: 'blur' }
        ]
      }
    }
  },

  mounted () {
    this.getList()
  },

  methods: {
    // 获取列表数据
    async getList () {
      this.loading = true
      try {
        const params = {
          page: this.pageNum,
          limit: this.pageSize,
          search: this.searchKey || undefined
        }
        const res = await getHomeModuleList(params)
        if (res.data.code === 0) {
          this.tableData = res.data.data || []
          this.pageTotal = res.data.total || 0
        } else {
          this.$Message.error(res.data.msg || '获取数据失败')
        }
      } catch (error) {
        this.$Message.error('网络请求失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch () {
      this.pageNum = 1
      this.getList()
    },

    // 分页
    changePage (page) {
      this.pageNum = page
      this.getList()
    },

    // 改变每页条数
    handlePageSize (size) {
      this.pageSize = size
      this.pageNum = 1
      this.getList()
    },

    // 新增
    handleAdd () {
      this.isEdit = false
      this.modalTitle = '新增模块'
      this.formData = {
        id: null,
        module_name: '',
        module_icon: '',
        module_color: '#1890FF',
        icon_color: '#FFFFFF',
        action_type: 1,
        action_value: '',
        extra_data: {
          appId: '',
          path: '',
          extraData: {}
        },
        status: 1,
        sort_order: 0
      }
      this.extraDataString = ''
      this.extraDataError = ''
      this.$nextTick(() => {
        this.$refs.moduleForm.resetFields()
      })
      this.modalVisible = true
    },

    // 编辑
    handleEdit (row) {
      this.isEdit = true
      this.modalTitle = '编辑模块'

      // 处理 extra_data 数据回填
      let extraData = { appId: '', path: '', extraData: {} }
      if (row.extra_data) {
        try {
          extraData = typeof row.extra_data === 'string'
            ? JSON.parse(row.extra_data)
            : row.extra_data
        } catch (e) {
          console.error('解析 extra_data 失败:', e)
        }
      }

      // 处理 status：兼容旧数据格式（字符串转数字）
      let status = row.status
      if (typeof status === 'string') {
        const statusMap = { normal: 1, hidden: 2, disabled: 3 }
        status = statusMap[status] || 1
      }

      this.formData = {
        id: row.id,
        module_name: row.module_name,
        module_icon: row.module_icon,
        module_color: row.module_color,
        icon_color: row.icon_color || '#FFFFFF',
        action_type: row.action_type,
        action_value: row.action_value,
        extra_data: extraData,
        status: status,
        sort_order: row.sort_order
      }

      // 如果是小程序类型，回填 extraData 字符串
      if (extraData.extraData && Object.keys(extraData.extraData).length > 0) {
        this.extraDataString = JSON.stringify(extraData.extraData, null, 2)
      } else {
        this.extraDataString = ''
      }
      this.extraDataError = ''

      this.modalVisible = true
    },

    // 提交
    handleSubmit () {
      this.$refs.moduleForm.validate(async (valid) => {
        if (valid) {
          // 如果是小程序类型，验证 extraData JSON 格式
          if (this.formData.action_type === 2 && this.extraDataString.trim()) {
            if (!this.validateExtraData()) {
              return
            }
          }

          this.submitLoading = true
          try {
            const data = { ...this.formData }
            delete data.id

            // 如果是跳转小程序，处理 extra_data
            if (data.action_type === 2) {
              data.action_value = '' // 跳转小程序时 action_value 可以为空
              data.extra_data = JSON.stringify(data.extra_data)
            } else {
              data.extra_data = null
            }

            let res
            if (this.isEdit) {
              res = await updateHomeModule(this.formData.id, data)
            } else {
              res = await createHomeModule(data)
            }

            if (res.data.code === 0) {
              this.$Message.success(this.isEdit ? '修改成功' : '新增成功')
              this.modalVisible = false
              this.getList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          } catch (error) {
            this.$Message.error('网络请求失败')
            console.error(error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 删除
    async handleDelete (row) {
      try {
        const res = await deleteHomeModule(row.id)
        if (res.data.code === 0) {
          this.$Message.success('删除成功')
          // 如果当前页只有一条数据且不是第一页，则返回上一页
          if (this.tableData.length === 1 && this.pageNum > 1) {
            this.pageNum--
          }
          this.getList()
        } else {
          this.$Message.error(res.data.msg || '删除失败')
        }
      } catch (error) {
        this.$Message.error('网络请求失败')
        console.error(error)
      }
    },

    // 获取状态徽标
    getStatusBadge (status) {
      const statusMap = {
        1: 'success',
        2: 'default',
        3: 'error',
        // 兼容旧格式
        normal: 'success',
        hidden: 'default',
        disabled: 'error'
      }
      return statusMap[status] || 'default'
    },

    // 获取状态文本
    getStatusText (status) {
      const statusMap = {
        1: '正常',
        2: '隐藏',
        3: '停用',
        // 兼容旧格式
        normal: '正常',
        hidden: '隐藏',
        disabled: '停用'
      }
      return statusMap[status] || '-'
    },

    // 验证 extraData JSON 格式
    validateExtraData () {
      this.extraDataError = ''
      const trimmedValue = this.extraDataString.trim()

      // 如果为空，设置为空对象
      if (!trimmedValue) {
        this.formData.extra_data.extraData = {}
        return true
      }

      try {
        const parsed = JSON.parse(trimmedValue)
        // 确保解析的是对象而不是其他类型
        if (typeof parsed !== 'object' || Array.isArray(parsed)) {
          this.extraDataError = 'JSON 格式不正确，必须是对象格式，如：{"key":"value"}'
          return false
        }
        this.formData.extra_data.extraData = parsed
        return true
      } catch (e) {
        this.extraDataError = 'JSON 格式不正确: ' + e.message
        return false
      }
    }
  }
}
</script>

<style scoped>
.search-con {
  margin-bottom: 20px;
}
.search-col {
  display: flex;
  align-items: center;
}
</style>
